// ชิ้นส่วนที่ใช้ร่วมกันของหมวด "วิชาการภาคศาสนา" (หัวหน้าวิชาการศาสนา pos 57)
//
// ทุกหน้าในหมวดนี้เริ่มจากคำถามเดียวกัน — ปีการศึกษาไหน ชั้นไหน ห้องไหน — จึงรวม
// แถบตัวกรองไว้ที่เดียว ไม่ให้แต่ละหน้าเขียน dropdown ชุดเดียวกันซ้ำแล้วเลื่อนออกจากกัน
//
// ป้ายสี/รูปนักเรียน/การแจ้งเตือน 503 ใช้ของหมวดติดตามนักเรียนภาคศาสนาต่อ
// (islamic-tracking/trackingShared.js) เพราะครูคนเดียวกันดูทั้งสองหมวด
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Form, Row } from 'react-bootstrap'
import axiosInstance from '../services/axiosInterceptor'
import { alertRequestError } from '../islamic-tracking/trackingShared'
import { isolate } from '../utils/bidi'
import ThaiDateInput from '../components/ThaiDateInput'

export const API = process.env.REACT_APP_API

export {
  StudentAvatar, LevelBadge, InfoTip, useDarkTables, alertRequestError, fmtDate,
  TONE_HEX, tint,
} from '../islamic-tracking/trackingShared'

// "5/2 (AIP)" — ป้ายห้องที่ใช้ทุกหน้า โปรแกรมต่อท้ายเฉพาะห้องพิเศษ เพราะห้องปกติ
// เป็น GP ทั้งหมด ต่อท้ายไปก็ไม่ได้บอกอะไรเพิ่มนอกจากทำให้แถวยาวขึ้น
export const roomLabel = (row) => {
  if (!row) return ''
  const base = `${row.ic_class ?? ''}/${isolate(row.ir_no ?? '')}`
  const prog = row.is_programCode && row.is_programCode !== 'GP' ? ` (${row.is_programCode})` : ''
  return base + prog
}

export const genderLabel = (t, g) => (g === 'm' ? t.common.male : g === 'f' ? t.common.female : '-')

// ---- ปีการศึกษา ----
// จำปีที่เลือกไว้ใน localStorage คีย์ "yearId" ตัวเดียวกับหน้าอื่นทั้งระบบ ครูจึงไม่ต้อง
// เลือกปีใหม่ทุกครั้งที่ข้ามหน้า
export const useStudyYears = () => {
  const [years, setYears] = useState([])
  const [yearId, setYearIdState] = useState(() => localStorage.getItem('yearId') || '')

  useEffect(() => {
    let alive = true
    axiosInstance.get(`${API}/getStudyYear`)
      .then(res => {
        if (!alive) return
        const list = Array.isArray(res.data) ? res.data : []
        setYears(list)
        const saved = localStorage.getItem('yearId')
        const valid = list.some(y => String(y.year_id) === String(saved))
        const next = valid ? saved : (list[0] ? String(list[0].year_id) : '')
        if (next) { localStorage.setItem('yearId', next); setYearIdState(next) }
      })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const setYearId = useCallback((id) => {
    const v = String(id ?? '')
    if (v) localStorage.setItem('yearId', v)
    setYearIdState(v)
  }, [])

  return { years, yearId, setYearId }
}

// ---- ตัวกรองชั้น / ห้อง / เพศ ----
// ตัวเลือกมาจาก server เสมอ (ห้องที่ผู้เรียกมีสิทธิ์เห็นในปีนั้น) ไม่ใช่รายการชั้นคงที่
// ฝั่ง client — ชั้นภาคศาสนาเปิด/ปิดตามปี และ pos ที่เห็นไม่เท่ากันจะได้รายการไม่เท่ากัน
export const useAcademicFilters = (yearId, t) => {
  const [classes, setClasses] = useState([])
  const [rooms, setRooms] = useState([])
  const [classId, setClassId] = useState('')
  const [roomId, setRoomId] = useState('')
  const [gender, setGender] = useState('')

  useEffect(() => {
    if (!yearId) return
    let alive = true
    axiosInstance.get(`${API}/islamic/academic/filterOptions/${yearId}`)
      .then(res => {
        if (!alive) return
        setClasses(res.data?.classes || [])
        setRooms(res.data?.rooms || [])
      })
      .catch(err => { if (alive) alertRequestError(t, err, t.common.error) })
    return () => { alive = false }
  }, [yearId, t])

  // เปลี่ยนชั้นแล้วห้องที่เลือกไว้อาจไม่อยู่ในชั้นใหม่ — ล้างทิ้ง ไม่ปล่อยให้ค้างจนได้
  // ผลลัพธ์ว่างโดยครูไม่รู้ว่าเพราะอะไร
  const visibleRooms = useMemo(() => {
    let list = rooms
    if (classId) list = list.filter(r => String(r.ic_id) === String(classId))
    if (gender) list = list.filter(r => r.ir_gender === gender)
    return list
  }, [rooms, classId, gender])

  useEffect(() => {
    if (roomId && !visibleRooms.some(r => String(r.ir_id) === String(roomId))) setRoomId('')
  }, [visibleRooms, roomId])

  const query = useMemo(() => {
    const p = new URLSearchParams()
    if (classId) p.set('classId', classId)
    if (roomId) p.set('roomId', roomId)
    if (gender) p.set('gender', gender)
    const s = p.toString()
    return s ? `?${s}` : ''
  }, [classId, roomId, gender])

  return {
    classes, rooms, visibleRooms,
    classId, setClassId, roomId, setRoomId, gender, setGender,
    query,
    reset: () => { setClassId(''); setRoomId(''); setGender('') },
  }
}

// ---- วันที่ ----
// ทำงานกับ 'YYYY-MM-DD' เป็นหลัก และประกอบวันจากส่วนประกอบเวลาท้องถิ่นเสมอ
// ห้ามใช้ toISOString().slice(0,10) เด็ดขาด — มันแปลงเป็น UTC ก่อน เครื่องที่ +7
// จะได้วันก่อนหน้าหนึ่งวันตั้งแต่เที่ยงคืนถึงเจ็ดโมงเช้า (ปัญหาเดียวกับที่ CLAUDE.md เตือน)
export const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const parseYmd = (s) => new Date(`${s}T00:00:00`)
export const todayYmd = () => ymd(new Date())

const addDays = (s, n) => { const d = parseYmd(s); d.setDate(d.getDate() + n); return ymd(d) }
// ตรึงวันที่ 1 ก่อนบวกเดือน ไม่งั้น 31 ม.ค. + 1 เดือน จะกลายเป็น 3 มี.ค.
const addMonths = (s, n) => { const d = parseYmd(s); d.setDate(1); d.setMonth(d.getMonth() + n); return ymd(d) }
const startOfMonth = (s) => { const d = parseYmd(s); d.setDate(1); return ymd(d) }
const endOfMonth = (s) => { const d = parseYmd(s); d.setMonth(d.getMonth() + 1, 0); return ymd(d) }

export const RANGE_MODES = ['day', 'week', 'month']

// ช่วงวันที่ของรายงาน — โหมดเดียวกันทุกหน้าในหมวดนี้
//   day   วันเดียว            · ปุ่มเลื่อนทีละวัน
//   week  7 วันจบที่วันที่เลือก · ปุ่มเลื่อนทีละ 7 วัน
//   month ทั้งเดือน            · ปุ่มเลื่อนทีละเดือน
// เก็บ "วันอ้างอิง" ไว้ตัวเดียวแล้วคำนวณ start/end จากโหมด — สลับโหมดไปมาจึงอยู่ที่
// ช่วงเวลาเดิม ไม่กระโดดกลับไปวันนี้ทุกครั้งที่กด
export const useDateRange = (initialMode = 'day') => {
  const [mode, setMode] = useState(initialMode)
  const [anchor, setAnchor] = useState(todayYmd)

  const { start, end } = useMemo(() => {
    if (mode === 'week') return { start: addDays(anchor, -6), end: anchor }
    if (mode === 'month') return { start: startOfMonth(anchor), end: endOfMonth(anchor) }
    return { start: anchor, end: anchor }
  }, [mode, anchor])

  const shift = useCallback((dir) => {
    setAnchor(a => (mode === 'month' ? addMonths(a, dir) : addDays(a, mode === 'week' ? dir * 7 : dir)))
  }, [mode])

  // เลื่อนไปข้างหน้าเกินวันนี้ไม่มีข้อมูลอยู่แล้ว ปิดปุ่มดีกว่าปล่อยให้กดแล้วได้หน้าว่าง
  const atPresent = end >= todayYmd()

  return {
    mode, setMode, anchor, setAnchor, start, end, shift, atPresent,
    goToday: () => setAnchor(todayYmd()),
  }
}

// วันที่แบบไทย — เดือนย่อในโหมดช่วง เพราะขึ้นเป็นคอลัมน์ตารางยาว ๆ
export const thaiDate = (s, { long = false } = {}) => {
  if (!s) return ''
  const d = parseYmd(String(s).slice(0, 10))
  if (isNaN(d)) return ''
  return d.toLocaleDateString('th-TH', long
    ? { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
    : { weekday: 'short', day: 'numeric', month: 'short' })
}

// ---- สถานะการเข้าเรียน ----
// สีชุดเดียวกับรายงานภาคสามัญ (reports/GeneralAttendanceReport.js) โดยตั้งใจ —
// หัวหน้าวิชาการดูรายงานทั้งสองภาค สีเดียวกันต้องแปลว่าเรื่องเดียวกัน
//
// ทั้งสี่เป็น "สถานะ" ไม่ใช่ชุดสีแยกประเภท: สีเหลือง/ฟ้ามีคอนทราสต์ต่ำกว่า 3:1 บนพื้นสว่าง
// จึงห้ามให้สีเป็นตัวบอกความหมายตัวเดียว — ทุกที่ที่ใช้ต้องมีตัวเลข/ป้ายชื่อกำกับเสมอ
// เรียงจากดีไปแย่ เพื่อให้แถบสัดส่วนของแต่ละแถวเทียบกันด้วยตาได้
export const ATT_STATUS = [
  { key: 'present', color: '#198754', icon: 'ri-check-line' },
  { key: 'late',    color: '#ffc107', icon: 'ri-time-line' },
  { key: 'leave',   color: '#0dcaf0', icon: 'ri-calendar-close-line' },
  { key: 'absent',  color: '#dc3545', icon: 'ri-close-line' },
]

// การ์ดตัวเลขหนึ่งใบ — ป้ายชื่อกับตัวเลขคงสีตามธีม สีสถานะอยู่ที่จุดนำหน้าอย่างเดียว
// (เหลือง/ฟ้าคอนทราสต์ต่ำเกินกว่าจะเอามาเป็นสีตัวอักษรให้อ่านออกทั้งสองธีมได้)
export const StatTile = ({ label, value, sub, color }) => (
  <Card className="card-one h-100">
    <Card.Body className="p-3">
      <div className="d-flex align-items-center gap-2 fs-xs text-secondary">
        {color && <StatusDot color={color} />}
        <span className="text-truncate">{label}</span>
      </div>
      <div className="fs-20 fw-semibold lh-1 mt-2">{value}</div>
      {sub && <div className="fs-xs text-secondary mt-1">{sub}</div>}
    </Card.Body>
  </Card>
)

// จุดสีนำหน้าป้ายชื่อ — ตัวอักษรคงสีตามธีมเสมอ สีอยู่ที่จุดอย่างเดียว
export const StatusDot = ({ color, size = 10 }) => (
  <span
    className="d-inline-block rounded-circle flex-shrink-0"
    style={{ width: size, height: size, background: color }}
  />
)

// แถบสัดส่วน 100% ของหนึ่งแถว — ช่องว่าง 2px ระหว่างท่อนเป็นสีพื้นการ์ด (โปร่งใส)
// จึงถูกต้องทั้งโหมดสว่างและมืดโดยไม่ต้องรู้ว่าพื้นหลังสีอะไร และไม่ตีเส้นขอบรอบท่อน
export const StatusBar = ({ t, counts, total, height = 10 }) => {
  if (!total) return <div className="rounded-pill" style={{ height, background: 'currentColor', opacity: 0.08 }} />
  return (
    <div className="d-flex" style={{ height, gap: 2 }}>
      {ATT_STATUS.map(({ key, color }) => {
        const v = Number(counts[key] || 0)
        if (!v) return null
        return (
          <div
            key={key}
            title={`${t.academic.attendance[key]} ${v} (${Math.round((v / total) * 100)}%)`}
            style={{ width: `${(v / total) * 100}%`, background: color, borderRadius: 4 }}
          />
        )
      })}
    </div>
  )
}

// ช่องเลือกปีการศึกษา — แยกออกมาเพราะบางหน้าต้องเลือกปีแต่ไม่ต้องใช้ตัวกรองชั้น/ห้อง/เพศ
// (หน้าการเข้าเรียนรายบุคคลค้นด้วยชื่อ/เลขประจำตัวอยู่แล้ว ตัวกรองห้องมีแต่จะทำให้ค้นไม่เจอ
// โดยครูไม่รู้ว่าเพราะอะไร) — ทั้งสองที่จึงใช้ตัวเดียวกัน ไม่ต้องคัดลอกรายการปีไปไว้สองที่
export const YearSelect = ({ t, years, yearId, setYearId }) => (
  <Col xs={6} md={3} lg={2}>
    <Form.Label className="fs-xs text-secondary mb-1">{t.academic.filterYear}</Form.Label>
    <Form.Select size="sm" value={yearId} onChange={e => setYearId(e.target.value)}>
      {/* tb_studyyear เก็บปีไว้ในคอลัมน์ year_year (พ.ศ. เป็นข้อความ) ไม่ใช่ year_name */}
      {years.map(y => <option key={y.year_id} value={y.year_id}>{t.common.studyYearShort(y.year_year)}</option>)}
    </Form.Select>
  </Col>
)

// แถบตัวกรองมาตรฐาน — หน้าที่ต้องการช่องเพิ่ม (ช่วงวันที่, ภาคเรียน, ค้นหา) ส่งมาทาง children
export const AcademicFilterBar = ({ t, years, yearId, setYearId, filters, children }) => (
  <Row className="g-2 align-items-end">
    <YearSelect t={t} years={years} yearId={yearId} setYearId={setYearId} />
    <Col xs={6} md={3} lg={2}>
      <Form.Label className="fs-xs text-secondary mb-1">{t.academic.filterClass}</Form.Label>
      <Form.Select size="sm" value={filters.classId} onChange={e => filters.setClassId(e.target.value)}>
        <option value="">{t.academic.allClasses}</option>
        {filters.classes.map(c => <option key={c.ic_id} value={c.ic_id}>{c.ic_class}</option>)}
      </Form.Select>
    </Col>
    <Col xs={6} md={3} lg={2}>
      <Form.Label className="fs-xs text-secondary mb-1">{t.academic.filterRoom}</Form.Label>
      <Form.Select size="sm" value={filters.roomId} onChange={e => filters.setRoomId(e.target.value)}>
        <option value="">{t.academic.allRooms}</option>
        {filters.visibleRooms.map(r => <option key={r.ir_id} value={r.ir_id}>{roomLabel(r)}</option>)}
      </Form.Select>
    </Col>
    <Col xs={6} md={3} lg={2}>
      <Form.Label className="fs-xs text-secondary mb-1">{t.academic.filterGender}</Form.Label>
      <Form.Select size="sm" value={filters.gender} onChange={e => filters.setGender(e.target.value)}>
        <option value="">{t.academic.allGenders}</option>
        <option value="m">{t.common.male}</option>
        <option value="f">{t.common.female}</option>
      </Form.Select>
    </Col>
    {children}
  </Row>
)

// แถบเลือกช่วงวันที่ — ปุ่มเลื่อน ก่อนหน้า/ถัดไป อยู่ติดกับช่องเลือกวัน เพราะการใช้งานจริง
// คือ "เปิดวันนี้แล้วกดย้อนไปเรื่อย ๆ" ไม่ใช่พิมพ์วันที่ทีละครั้ง ส่วน date picker ไว้ให้
// กระโดดไปวันที่เจาะจง โหมดเดือนใช้ช่อง type="month" เพราะเลือกวันในเดือนไม่มีความหมาย
export const DateRangeBar = ({ t, range }) => {
  const { mode, setMode, anchor, setAnchor, start, end, shift, atPresent, goToday } = range
  const label = mode === 'day'
    ? thaiDate(start, { long: true })
    : mode === 'month'
      ? parseYmd(start).toLocaleDateString('th-TH', { month: 'long', year: 'numeric' })
      : `${thaiDate(start)} – ${thaiDate(end)}`

  return (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <div className="btn-group btn-group-sm" role="group">
        {RANGE_MODES.map(m => (
          <Button
            key={m}
            variant={mode === m ? 'primary' : 'outline-secondary'}
            onClick={() => setMode(m)}
          >
            {t.academic.attendance[`mode_${m}`]}
          </Button>
        ))}
      </div>

      <div className="btn-group btn-group-sm" role="group">
        <Button variant="outline-secondary" onClick={() => shift(-1)} title={t.academic.attendance.prev}>
          <i className="ri-arrow-left-s-line" />
        </Button>
        <Button variant="outline-secondary" onClick={() => shift(1)} disabled={atPresent} title={t.academic.attendance.next}>
          <i className="ri-arrow-right-s-line" />
        </Button>
      </div>

      {mode === 'month' ? (
        <Form.Control
          type="month"
          size="sm"
          style={{ width: 150 }}
          value={anchor.slice(0, 7)}
          max={todayYmd().slice(0, 7)}
          onChange={e => e.target.value && setAnchor(`${e.target.value}-01`)}
        />
      ) : (
        <ThaiDateInput
          size="sm"
          style={{ width: 160 }}
          value={anchor}
          max={todayYmd()}
          onChange={v => v && setAnchor(v)}
        />
      )}

      <Button size="sm" variant="outline-primary" onClick={goToday} disabled={anchor === todayYmd()}>
        {t.academic.attendance.today}
      </Button>

      <span className="fs-sm fw-semibold ms-auto">{label}</span>
    </div>
  )
}
